/**
 * App entry point
 *
 * - Loads `app.tsx`, which mounts the UI and emits "ui-ready"
 * - Waits for "ui-ready" before loading `main.app.ts` to ensure no events are missed
 * - Emits "load" after receiving the "initialized" event triggered by app.init()
 * - Initializes Sentry in production
 */

import emitter from "@webcore/core/events/eventemitter.ts";
import * as Sentry from "@sentry/react";

import("./app");
const params = new URLSearchParams(window.location.search);

// Global: prevent iOS double‑tap zoom and pinch zoom reliably
// This applies app-wide so individual components don't have to fight Safari defaults
(() => {
  try {
    let lastTouchEnd = 0;
    document.addEventListener(
      "touchend",
      (e) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      },
      { passive: false }
    );

    document.addEventListener(
      "touchstart",
      (e) => {
        if (e.touches && e.touches.length > 1) {
          e.preventDefault();
        }
      },
      { passive: false }
    );
  } catch {}
})();

// temp: making true for PROD
if (import.meta.env.DEV || params.has('dev')) {
  import("./utils/stats").then(({ initStats }) => initStats()).catch(() => {});
  import("./utils/eruda").then(({ initEruda }) => initEruda()).catch(() => {});
}

emitter.on("ui-ready", () => {
  import("./main.app");  
})

//----------------------------------------------------------------

emitter.on("initialized", () => {
  emitter.emit("load");
});

if (!import.meta.env.DEV) {
  const SENTRY_INIT_KEY = 'ej_sentry_initialized';
  
  try {
    const alreadyInitialized = sessionStorage.getItem(SENTRY_INIT_KEY) === '1';
    
    if (!alreadyInitialized) {
      Sentry.init({
        dsn: "https://6b42d6ee788dcf6cdb4283113572fb5a@o1388406.ingest.us.sentry.io/4510163767263232",
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 1.0,
      });
      
      sessionStorage.setItem(SENTRY_INIT_KEY, '1');
    }
  } catch (error) {
    console.warn('Sentry initialization failed:', error);
  }
}
